import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import BackButton from '../../components/BackButton';
import { maskVpa } from '../../utils/maskVpa';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const ShopTransactions = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');

  // Dropdown lists
  const [allStateHeads, setAllStateHeads] = useState([]);
  const [allQpayCenters, setAllQpayCenters] = useState([]);
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);

  // Filters State
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all');
  const [stateHeadFilter, setStateHeadFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]

  // Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchDropdownOptions();
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userId, shopId, districtFilter, villageFilter, stateHeadFilter, qpayCenterFilter, paymentMode]); // [CASH-MODE] paymentMode

  const fetchDropdownOptions = async () => {
    try {
      const [userRes, districtRes, shopRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/users-with-pincodes/${userId}`),
        axios.get(`${API_BASE_URL}/districts`),
        axios.get(`${API_BASE_URL}/shops/${userId}?progress=6&page=1&limit=500`),
      ]);

      if (userRes.data && Array.isArray(userRes.data)) {
        const sh = userRes.data.filter((u) => u.level === 2);
        const qc = userRes.data.filter((u) => u.level === 4);
        setAllStateHeads(sh);
        setAllQpayCenters(qc);
      }

      if (districtRes.data && Array.isArray(districtRes.data)) {
        const dists = districtRes.data.map((d) => d.districtName || d.name).filter(Boolean);
        setAllDistrictsList(Array.from(new Set(dists)));
      }

      if (shopRes.data && shopRes.data.shops) {
        const consts = shopRes.data.shops
          .map((s) => s.village || s.constituency || s.city || s.taluk)
          .filter(Boolean);
        setAllConstituenciesList(Array.from(new Set(consts)));
      }
    } catch (e) {
      console.error('Error fetching dropdown options:', e);
    }
  };

  const fetchTransactions = async () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before end date.');
      setOpenDialog(true);
      return;
    }

    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      const token = sessionStorage.getItem('token');

      const payload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        shopId: shopId || 'all',
        district: districtFilter,
        village: villageFilter,
        stateHeadId: stateHeadFilter,
        qpayCenterId: qpayCenterFilter,
        paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
      };

      const response = await axios.post(
        `${API_BASE_URL}/shopReport/totalShopTransactions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.status === 'SUCCESS') {
        const txList = response.data.data.transactionsList || [];
        setTransactions(txList);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Dropdown lists
  const combinedDistricts = Array.from(
    new Set([...allDistrictsList, ...transactions.map((t) => t.district).filter(Boolean)])
  );
  const combinedConstituencies = Array.from(
    new Set([...allConstituenciesList, ...transactions.map((t) => t.village).filter(Boolean)])
  );

  // Filtered & Sorted transactions
  // [CASH-MODE] client-side mode filter as a safety net (no-op for ALL)
  const filteredTransactions = filterRowsByMode(transactions, paymentMode)
    .filter((tx) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase().trim();
        const mId = String(tx.merchantId || '').toLowerCase();
        const mob = String(tx.mobile || tx.customerMobile || '').toLowerCase();
        const email = String(tx.email || tx.customerEmail || '').toLowerCase();
        const cName = String(tx.customerName || '').toLowerCase();
        const cId = String(tx.customerId || '').toLowerCase();

        const match =
          mId.includes(term) ||
          mob.includes(term) ||
          email.includes(term) ||
          cName.includes(term) ||
          cId.includes(term);
        if (!match) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.dateTime);
      const dateB = new Date(b.dateTime);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

  const handleExportCSV = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) return;
    const headers = [
      'SL No',
      'Date & Time',
      'Merchant ID',
      'Customer ID',
      'Customer Name',
      'Customer VPA ID',
      'Type',
      'Amount',
      'Commission %',
      'Commission Amount',
      'Merchant Amount',
      'Transaction ID',
      'Status',
      'Mode', // [CASH-MODE]
    ];
    const csvRows = [headers.join(',')];

    filteredTransactions.forEach((tx, idx) => {
      const rawVpa = tx.customerVPA || 'N/A';
      // Hashed VPA for non-superadmin (userLevel !== 1)
      const vpaDisplay =
        userLevel === 1 ? rawVpa : rawVpa !== 'N/A' ? maskVpa(rawVpa, userLevel || 2) : 'N/A';

      let dtStr = 'N/A';
      if (tx.dateTime) {
        try {
          dtStr = format(new Date(tx.dateTime), 'dd-MM-yyyy HH:mm:ss');
        } catch (e) {
          dtStr = String(tx.dateTime);
        }
      }

      csvRows.push(
        [
          idx + 1,
          `"${dtStr}"`,
          `"${tx.merchantId || 'N/A'}"`,
          `"${tx.customerId || 'N/A'}"`,
          `"${tx.customerName || 'N/A'}"`,
          `"${vpaDisplay}"`,
          `"${tx.type || 'Credit'}"`,
          (tx.amount || 0).toFixed(2),
          (tx.commissionPercent || 0).toFixed(2),
          (tx.commissionAmount || 0).toFixed(2),
          (tx.merchantAmount || 0).toFixed(2),
          `"${tx.transactionId || 'N/A'}"`,
          `"${tx.status || 'SUCCESS'}"`,
          `"${paymentModeLabel(tx)}"`, // [CASH-MODE]
        ].join(',')
      );
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `Merchants_Transaction_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'SL No',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const index = filteredTransactions.findIndex((t) => t.id === params.row.id);
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : params.row.id}
          </Typography>
        );
      },
    },
    {
      field: 'dateTime',
      headerName: 'Date & Time',
      width: 170,
      renderCell: (params) => {
        let dtStr = 'N/A';
        if (params.value) {
          try {
            dtStr = format(new Date(params.value), 'dd-MM-yyyy HH:mm:ss');
          } catch (e) {
            dtStr = String(params.value);
          }
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
            {dtStr}
          </Typography>
        );
      },
    },
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerId',
      headerName: 'Customer ID',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customerVPA',
      headerName: 'Customer VPA ID',
      width: 200,
      renderCell: (params) => {
        const rawVpa = params.value || 'N/A';
        // HASHED for all logins EXCEPT super admin (userLevel === 1)
        const vpaDisplay =
          userLevel === 1 ? rawVpa : rawVpa !== 'N/A' ? maskVpa(rawVpa, userLevel || 2) : 'N/A';

        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
            {vpaDisplay}
          </Typography>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Credit'}
          size="small"
          color={params.value === 'Debit' ? 'warning' : 'primary'}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    // [CASH-MODE] UPI / Cash
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 100,
      renderCell: (params) => <PaymentModeChip row={params.row} />,
    },
    {
      field: 'actions',
      headerName: 'Action Button',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          color="info"
          startIcon={<VisibilityIcon />}
          onClick={() => {
            setSelectedTxn(params.row);
            setDetailModalOpen(true);
          }}
          sx={{ fontWeight: 600 }}
        >
          Details
        </Button>
      ),
    },
  ];

  const rows = filteredTransactions.map((item, index) => ({
    id: item.id || index + 1,
    ...item,
  }));

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br />
      <br />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ width: '100%', position: 'relative' }}>
          {/* Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  Merchants Transaction Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Detailed Transaction History & Customer VPAs
                </Typography>
              </Box>

              {/* Action Buttons: CSV Export (Super Admin Level 1 Only) */}
              {userLevel === 1 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCSV}
                    disabled={filteredTransactions.length === 0}
                    sx={{ fontWeight: 700 }}
                  >
                    Export to CSV
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Filter Section */}
            <Grid container spacing={2} alignItems="center">
              {/* Date Filters */}
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                  maxDate={new Date()}
                  inputFormat="dd-MM-yyyy"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                  maxDate={new Date()}
                  inputFormat="dd-MM-yyyy"
                />
              </Grid>

              {/* Search by Merchant ID / Mobile / Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by Merchant ID, Mobile or Email"
                  placeholder="Search Merchant ID, Mobile, Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* District wise filter */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>District wise</InputLabel>
                  <Select
                    value={districtFilter}
                    label="District wise"
                    onChange={(e) => setDistrictFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Districts</MenuItem>
                    {combinedDistricts.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Constituency wise filter */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Constituency wise</InputLabel>
                  <Select
                    value={villageFilter}
                    label="Constituency wise"
                    onChange={(e) => setVillageFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Constituencies</MenuItem>
                    {combinedConstituencies.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* State Head wise filter (Level 2 Users) */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>State Head wise</InputLabel>
                  <Select
                    value={stateHeadFilter}
                    label="State Head wise"
                    onChange={(e) => setStateHeadFilter(e.target.value)}
                  >
                    <MenuItem value="all">All State Heads</MenuItem>
                    {allStateHeads.map((sh) => {
                      const val = sh.stateHeadId || sh.displayId || sh.id;
                      return (
                        <MenuItem key={sh.id} value={val}>
                          {sh.fullName} (ID: {val})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Q-Pay Centre wise filter (Level 4 Users) */}
              <Grid item xs={6} sm={2.2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Q-Pay Centre wise</InputLabel>
                  <Select
                    value={qpayCenterFilter}
                    label="Q-Pay Centre wise"
                    onChange={(e) => setQpayCenterFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Q-Pay Centers</MenuItem>
                    {allQpayCenters.map((qc) => {
                      const val = qc.stateHeadId || qc.displayId || qc.id;
                      return (
                        <MenuItem key={qc.id} value={val}>
                          {qc.fullName} (ID: {val})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* [CASH-MODE] Payment Mode filter */}
              <Grid item xs={6} sm={3}>
                <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
              </Grid>

              {/* Sort by Date Toggle */}
              <Grid item xs={12} sm={0.8} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title={`Sort by Date (${sortOrder === 'desc' ? 'Newest First' : 'Oldest First'})`}>
                  <IconButton
                    color="primary"
                    onClick={() => setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                    sx={{ border: '1px solid #cbd5e1', borderRadius: 2 }}
                  >
                    <SortIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* Table / Loader */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <React.Fragment>
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  rowHeight={60}
                />
              </Box>

              {/* Summary Paper */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Showing {filteredTransactions.length} Transaction Record(s)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 800, color: 'success.main' }}>
                  Total Transaction Amount: ₹
                  {filteredTransactions
                    .reduce((sum, t) => sum + (t.amount || 0), 0)
                    .toFixed(2)}
                </Typography>
              </Paper>

              {/* [CASH-MODE] UPI vs Cash totals over the rows shown (all rows are loaded, so this matches the total above) */}
              <PaymentModeTotals
                totals={totalsByModeFromRows(filteredTransactions, (t) => t.amount)}
                amountKey="amount"
                title="Transactions"
              />
            </React.Fragment>
          )}

          {/* Transaction Details Modal */}
          <Dialog
            open={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Transaction Details
              </Typography>
              <IconButton size="small" onClick={() => setDetailModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent dividers>
              {selectedTxn && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date & Time:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {selectedTxn.dateTime
                        ? format(new Date(selectedTxn.dateTime), 'dd-MM-yyyy HH:mm:ss')
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Merchant ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                      {selectedTxn.merchantId || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Customer ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedTxn.customerId || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Customer Name:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedTxn.customerName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Customer VPA ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                      {userLevel === 1
                        ? selectedTxn.customerVPA || 'N/A'
                        : selectedTxn.customerVPA !== 'N/A'
                        ? maskVpa(selectedTxn.customerVPA, userLevel || 2)
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Type:
                    </Typography>
                    <Chip label={selectedTxn.type || 'Credit'} size="small" color="primary" />
                  </Grid>
                  {/* [CASH-MODE] */}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Payment Mode:
                    </Typography>
                    <PaymentModeChip row={selectedTxn} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {selectedTxn.status || 'SUCCESS'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Amount:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>
                      ₹{(selectedTxn.amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setDetailModalOpen(false)}
                variant="contained"
                color="primary"
                sx={{ fontWeight: 700 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle sx={{ fontWeight: 700 }}>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="primary" variant="contained">
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default ShopTransactions;